import { createActions } from 'redux-actions';
import { batch } from 'react-redux';
import registrationApi from '../../../api/registrationApi';
import { selectAbn, selectTrusteeAbn, selectMainPortal } from '../../../selectors';
// eslint-disable-next-line import/no-cycle
import { markCurrentStepAsCompletedAndGoToNextStep, resetCurrentStep } from '../steps/actions';
import { showMainLoading, hideMainLoading } from '../main/actions';
import { ACTION_PREFIX, actionTypes } from './constants';
import { getActionOptions } from '../../../utils/reduxactionsutils';

const deedUploadStatus = {
  hasError: false,
};

export const {
  updateIndividualTrustee,
  updateTrusteeValidationResult,
  resetTrusteeValidationResult,
  updateTrusteeNames,
} = createActions(
  actionTypes.UPDATE_INDIVIDUAL_TRUSTEE,
  actionTypes.UPDATE_TRUSTEE_VALIDATION_RESULT,
  actionTypes.RESET_TRUSTEE_VALIDATION_RESULT,
  actionTypes.UPDATE_TRUSTEE_NAMES,
  getActionOptions(ACTION_PREFIX),
);

export const uploadDeed = (files, abn, dispatch) => {
  const promises = [];
  Array.prototype.forEach.call(files, (file) => {
    const formData = new FormData();
    formData.append('fileContent', file);
    const promise = registrationApi
      .uploadDeed(abn, formData)
      .then((result) => {
        if (result.data.isUploaded === false) {
          deedUploadStatus.hasError = true;
        }
        return true;
      })
      .catch(() => {
        deedUploadStatus.hasError = true;
        return false;
      })
      .finally(() => dispatch(hideMainLoading()));
    promises.push(promise);
  });
  return promises;
};

export const saveTrusteeDetails = (values, callback) => (dispatch, getState) => {
  deedUploadStatus.hasError = false;
  const { trusteeNames, isIndividual } = values;
  const abn = selectAbn(getState())?.id ?? null;
  const relatedEntityId = selectTrusteeAbn(getState())?.id ?? null;

  if (!abn) {
    if (callback) {
      callback();
    }
    return;
  }

  if (isIndividual === 'false' && !relatedEntityId) {
    if (callback) {
      callback();
    }
    return;
  }

  batch(() => {
    dispatch(showMainLoading('Posting Individual Trust Details...'));
  });

  const requestBody = {
    relatedEntityId,
    deedReferences: '',
    trusteeNames,
    isIndividual,
  };

  Promise.all(uploadDeed(values.files, abn, dispatch)).then(() => {
    if (deedUploadStatus.hasError === false) {
      if (values.files.length > 1) {
        Array.prototype.forEach.call(values.files, (file) => {
          if (requestBody.deedReferences === '') {
            requestBody.deedReferences = file.name;
          } else {
            requestBody.deedReferences = `${file.name},${requestBody.deedReferences}`;
          }
        });
      } else if (values.files.length === 1) {
        requestBody.deedReferences = values.files[0].name;
      }
      registrationApi
        .saveTrustDetails(abn, requestBody)
        .then(({ data }) => {
          dispatch(updateTrusteeNames({ trusteeNames: values.trusteeNames }));

          if (callback) {
            callback();
          }
          const {
            output: { isError, errorMessage, links },
            isSuccess,
            messages,
          } = data;

          if (isError) {
            dispatch(updateIndividualTrustee({ message: errorMessage }));
            return;
          }

          if (!isSuccess) {
            const message =
              messages && messages.length
                ? messages[0]
                : 'An unexpected error has occured while saving the trustee details';
            dispatch(updateIndividualTrustee({ message }));
            return;
          }

          if (links && links.length) {
            batch(() => {
              dispatch(resetCurrentStep());
              dispatch(markCurrentStepAsCompletedAndGoToNextStep(links));
            });
          }
        })
        .catch(() => {
          if (callback) {
            callback();
          }
          dispatch(
            updateIndividualTrustee({
              message: 'An unexpected error has occured while uploading deed files',
            }),
          );
        })
        .finally(() => {
          dispatch(hideMainLoading());
        });
    } else {
      batch(() => {
        dispatch(
          updateIndividualTrustee({
            message: 'An unexpected error has occured while uploading deed files',
          }),
        );
        if (callback) {
          callback();
        }
      });
    }
  });
};

export function checkTrusteeEligibility(selectedAbn) {
  return (dispatch, getState) => {
    const state = getState();
    const parentAbn = selectAbn(state);
    const portal = selectMainPortal(state);

    const setError = (message) => {
      dispatch(
        updateTrusteeValidationResult({
          abn: selectedAbn,
          isValidated: true,
          isEligible: false,
          message,
        }),
      );
    };

    batch(() => {
      dispatch(showMainLoading('Validating Trustee as a Company...'));
      dispatch(resetCurrentStep());
    });

    registrationApi
      .validateRelatedEntity(parentAbn.id, selectedAbn.id, portal)
      .then(({ data }) => {
        const { abn, entityDetails, isError, errorMessage } = data.output;

        if (abn !== selectedAbn.id) {
          return;
        }

        if (isError) {
          setError(errorMessage);
          return;
        }

        if (entityDetails.isValid && entityDetails.isActive) {
          batch(() => {
            dispatch(
              updateTrusteeValidationResult({
                abn: selectedAbn,
                isValidated: true,
                isEligible: true,
                message: 'This trustee is eligible',
              }),
            );
          });
        } else {
          setError('Unfortunately, we do not support unregistered businesses at the moment.');
        }
      })
      // eslint-disable-next-line no-unused-vars
      .catch((_reason) => {
        setError('Something went wrong while checking eligibility');
      })
      .finally(() => dispatch(hideMainLoading()));
  };
}
